import React, { useCallback } from 'react';
import { Box, IconButton, Tooltip } from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import PropTypes from 'prop-types';

/**
 * The window the Integral Delta analysis runs over.
 *
 * <h2>Empty means the WHOLE history, not the loaded chart</h2>
 *
 * The backend treats an absent bound as "as far as the data goes", so clearing both boxes restores
 * the full-history run rather than silently scoping to whatever happens to be on screen. That
 * replaced exactly such a silent scoping: the button used to send the chart's own span, which made
 * the same click give different answers depending on how far the user had scrolled.
 *
 * <h2>The reversed pair is prevented here, not refused there</h2>
 *
 * The API rejects `startDate > endDate` with a 400, which is correct for a caller that got it
 * wrong — but a person dragging a date around should not be able to reach that state at all. Each
 * picker constrains the other, so the pair is always orderable.
 *
 * <p>The pickers hold dayjs objects and hand dayjs objects back, so the parent keeps ONE
 * representation. {@link toApiDate} converts at the edge, where the request is built — round
 * tripping through strings just to refill the pickers would lose the partially-typed state the
 * picker itself is managing.
 */

/**
 * A picker value as the API wants it, or undefined.
 *
 * <p>Undefined for a half-typed or invalid entry, NOT the string "Invalid Date". The backend parses
 * dates strictly and refuses what it cannot read, so sending garbage would raise an error about a
 * box the user has not finished filling in.
 */
export const toApiDate = v =>
  (v && typeof v.isValid === 'function' && v.isValid() ? v.format('YYYY-MM-DD') : undefined);

const AnalysisDateRange = ({ start, end, onChange, disabled }) => {
  const emit = useCallback((nextStart, nextEnd) => {
    if (!onChange) return;
    onChange({ start: nextStart || null, end: nextEnd || null });
  }, [onChange]);

  const hasAny = Boolean(start || end);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <DatePicker
          label="Analysis from"
          value={start || null}
          disabled={disabled}
          maxDate={end || undefined}
          onChange={v => emit(v, end)}
          format="YYYY-MM-DD"
          slotProps={{
            textField: {
              size: 'small',
              sx: { width: 165 },
              variant: 'outlined',
              // NO `placeholder` here. x-date-pickers v8 renders a sectioned field, which ignores
              // it entirely - the prop looked like it documented "empty means all history" and in
              // fact displayed nothing. The meaning lives in the helper text below, which does
              // render, and in the clear button's tooltip.
              helperText: start || end ? ' ' : 'empty = all history',
            },
            field: { clearable: true, onClear: () => emit(null, end) },
          }}
        />
        <DatePicker
          label="Analysis to"
          value={end || null}
          disabled={disabled}
          minDate={start || undefined}
          onChange={v => emit(start, v)}
          format="YYYY-MM-DD"
          slotProps={{
            textField: {
              size: 'small',
              sx: { width: 165 },
              variant: 'outlined',
              helperText: start || end ? ' ' : 'empty = all history',
            },
            field: { clearable: true, onClear: () => emit(start, null) },
          }}
        />
        <Tooltip arrow title="Clear both — the analysis reverts to the whole stored history">
          {/* A disabled button fires no events, so the tooltip needs an element that does. */}
          <span>
            <IconButton
              size="small"
              aria-label="clear analysis date range"
              disabled={disabled || !hasAny}
              onClick={() => emit(null, null)}
            >
              <ClearIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    </LocalizationProvider>
  );
};

AnalysisDateRange.propTypes = {
  /** dayjs objects, or null. Held by the parent so the analysis button can read them. */
  start: PropTypes.object,
  end: PropTypes.object,
  /** Called with `{start, end}` as dayjs objects, or null for an unset bound. */
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
};

export default AnalysisDateRange;
